import { FormEvent, useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { useSeoMeta } from "@unhead/react"
import toast, { Toaster } from "react-hot-toast"
import {Sparkles, Mail, Phone, Calendar, ShieldCheck, ArrowRight, Send, Gift, Baby} from 'lucide-react'
import { contactEmail, contactPhone, fullAddress, images, serviceArea, siteName, calendlyUrl, prestationsBebe } from "@/data/site"

const CONTACT_FUNCTION_URL = "https://api.lumi.new/v1/functions/p485856725183959040/sendContactEmail"

interface FormErrors {
  name?: string
  email?: string
  phone?: string
  message?: string
}

function Contact() {
  const location = useLocation()
  const [sending, setSending] = useState(false)
  const [formLoadedAt, setFormLoadedAt] = useState<number>(Date.now())
  const [honeypot, setHoneypot] = useState("")
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    moment: "Grossesse",
    giftPrestation: "",
    recipientName: "",
    babyWorkshop: "",
    message: "",
  })

  useEffect(() => {
    setFormLoadedAt(Date.now())
    const searchParams = new URLSearchParams(location.search)
    const sujet = searchParams.get("sujet")
    const prestationParam = searchParams.get("prestation")

    if (sujet === "carte-cadeau" || prestationParam) {
      setForm((prev) => ({
        ...prev,
        moment: "Carte Cadeau",
        giftPrestation: prestationParam || prev.giftPrestation || "Massage \"Bien-Naître\" (prénatal) - 70 €",
      }))
    }
  }, [location.search])

  useSeoMeta({
    title: `Contact · ${siteName}`,
    description: "Prendre rendez-vous pour une rencontre découverte.",
    ogTitle: `Contact · ${siteName}`,
    ogDescription: "Prendre rendez-vous pour une rencontre découverte.",
    ogImage: images.mother,
    twitterCard: "summary_large_image",
  })

  const validateField = (name: string, value: string): string | undefined => {
    const trimmed = value.trim()
    switch (name) {
      case "name":
        if (!trimmed) return "Veuillez renseigner votre nom."
        if (trimmed.length < 2) return "Le nom doit comporter au moins 2 caractères."
        if (trimmed.length > 80) return "Le nom ne peut pas dépasser 80 caractères."
        return undefined
      case "email":
        if (!trimmed) return "Veuillez renseigner votre adresse e-mail."
        if (trimmed.length > 120) return "L'adresse e-mail est trop longue."
        if (!/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(trimmed)) {
          return "Format d'adresse e-mail invalide (ex: exemple@domaine.fr)."
        }
        return undefined
      case "phone":
        if (trimmed) {
          const cleaned = trimmed.replace(/[\s.-]/g, "")
          if (!/^(?:(?:\+|00)[1-9]\d{6,14}|0[1-9]\d{8})$/.test(cleaned) || trimmed.length > 25) {
            return "Numéro de téléphone invalide (ex: 06 12 34 56 78)."
          }
        }
        return undefined
      case "message":
        if (!trimmed) return "Veuillez rédiger un message."
        if (trimmed.length < 10) return "Votre message doit contenir au moins 10 caractères."
        if (trimmed.length > 3000) return "Le message ne peut pas dépasser 3000 caractères."
        return undefined
      default:
        return undefined
    }
  }

  const validateAll = (): boolean => {
    const newErrors: FormErrors = {
      name: validateField("name", form.name),
      email: validateField("email", form.email),
      phone: validateField("phone", form.phone),
      message: validateField("message", form.message),
    }
    setErrors(newErrors)
    setTouched({ name: true, email: true, phone: true, message: true })
    return !Object.values(newErrors).some(Boolean)
  }

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    const error = validateField(field, (form as any)[field] || "")
    setErrors((prev) => ({ ...prev, [field]: error }))
  }

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (touched[field]) {
      const error = validateField(field, value)
      setErrors((prev) => ({ ...prev, [field]: error }))
    }
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!validateAll()) {
      toast.error("Merci de corriger les champs signalés en rouge.")
      return
    }

    setSending(true)
    try {
      const response = await fetch(CONTACT_FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          message:
            form.moment === "Carte Cadeau"
              ? `[Demande de Carte Cadeau]\nPrestation choisie : ${form.giftPrestation || "Non spécifiée"}\nBénéficiaire : ${form.recipientName || "Non spécifié"}\n\nMessage : ${form.message}`
              : form.moment === "Atelier bébé"
                ? `[Demande d'atelier bébé]\nAtelier choisi : ${form.babyWorkshop || "Non spécifié"}\n\nMessage : ${form.message}`
                : form.message,
          website: honeypot, // Honeypot anti-bot
          timestamp: formLoadedAt, // Timing anti-bot
        }),
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data?.error || "Une erreur est survenue lors de l'envoi.")
      }

      setForm({ name: "", email: "", phone: "", moment: "Grossesse", giftPrestation: "", recipientName: "", babyWorkshop: "", message: "" })
      setHoneypot("")
      setErrors({})
      setTouched({})
      setFormLoadedAt(Date.now())
      toast.success("Message envoyé avec succès ! Un e-mail de confirmation vous a été envoyé.")
    } catch (error: any) {
      toast.error(error?.message || "L'envoi du message a échoué. Merci de réessayer.")
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="text-[#2d221b]">
      <Toaster position="top-center" />
      <section className="mx-auto grid max-w-6xl gap-10 px-5 py-14 md:grid-cols-[0.9fr_1.1fr] md:px-8">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#d6c4b6] bg-[#efe1d4]/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-[#b85c38]">
            <Sparkles size={14} />
            <span>Contact & Rendez-vous</span>
          </div>
          <h1 className="mt-4 font-display text-4xl font-medium leading-tight text-[#2d221b] md:text-6xl">
            Écrivons-nous
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-[#5e4b3e] md:text-base">
            La première rencontre découverte est offerte. Indiquez-moi où vous en êtes : je vous proposerai un créneau sous 48 heures.
          </p>
          
          <div className="mt-6 overflow-hidden rounded-3xl border border-[#dfd0c2] bg-white p-2.5 shadow-warm-xs">
            <img
              src={images.mother}
              alt="Moment de tendresse après la naissance"
              loading="lazy"
              className="h-64 w-full rounded-2xl object-cover"
            />
          </div>

          <div className="mt-6 space-y-3 rounded-2xl border border-[#dfd0c2] bg-[#fbf7f2] p-5 shadow-warm-xs text-xs text-[#5e4b3e] md:text-sm">
            <p className="leading-relaxed">{serviceArea}</p>
            <div className="flex flex-col gap-2 pt-2 border-t border-[#ede0d2]">
              <p className="inline-flex items-center gap-2 font-medium text-[#2d221b]">
                <span className="text-[#b85c38]">•</span>
                <span>{fullAddress}</span>
              </p>
              <a href={`mailto:${contactEmail}`} className="inline-flex items-center gap-2 font-medium text-[#2d221b] hover:text-[#b85c38] transition-colors">
                <Mail size={15} className="text-[#b85c38]" />
                <span>{contactEmail}</span>
              </a>
              <a href={`tel:${contactPhone.replace(/\s+/g, '')}`} className="inline-flex items-center gap-2 font-medium text-[#2d221b] hover:text-[#b85c38] transition-colors">
                <Phone size={15} className="text-[#b85c38]" />
                <span>{contactPhone}</span>
              </a>
            </div>
          </div>

          {/* Carte Réservation Calendly */}
          <div className="mt-6 rounded-3xl border border-[#dfd0c2] bg-gradient-to-br from-[#efe1d4] to-[#fbf7f2] p-6 shadow-warm-xs">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-[#b85c38]" />
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">Réservation directe</p>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-[#5e4b3e] md:text-sm">
              Vous pouvez également choisir votre créneau directement en ligne sur mon agenda :
            </p>
            <a
              href={calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#b85c38] to-[#9d4c2e] px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-white shadow-warm-xs transition hover:shadow-warm-sm hover:scale-[1.02]"
            >
              <span>Réserver sur Calendly</span>
              <ArrowRight size={13} />
            </a>
          </div>
        </div>

        <form onSubmit={onSubmit} className="rounded-3xl border border-[#dfd0c2] bg-[#fbf7f2] p-6 shadow-warm-sm md:p-10" noValidate>
          {/* Honeypot field - Hidden from real users, only filled by automated bots */}
          <div className="hidden" aria-hidden="true" style={{ display: "none" }}>
            <label htmlFor="hp_website">Site web</label>
            <input
              id="hp_website"
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <label className="block text-xs font-semibold uppercase tracking-[0.12em] text-[#5e4b3e]">
            Nom & Prénom <span className="text-[#b85c38]">*</span>
            <input
              value={form.name}
              onChange={(event) => handleChange("name", event.target.value)}
              onBlur={() => handleBlur("name")}
              maxLength={80}
              required
              className={`mt-2 w-full rounded-2xl border px-4 py-3 text-sm text-[#2d221b] outline-none transition-colors ${
                touched.name && errors.name
                  ? "border-red-400 bg-red-50/40 focus:border-red-500"
                  : "border-[#d8c8b9] bg-white focus:border-[#b85c38] focus:ring-2 focus:ring-[#b85c38]/15"
              }`}
              autoComplete="name"
              placeholder="Votre prénom et nom"
            />
            {touched.name && errors.name && (
              <span className="mt-1.5 block text-xs font-normal normal-case text-red-600">{errors.name}</span>
            )}
          </label>

          <label className="mt-5 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5e4b3e]">
            E-mail <span className="text-[#b85c38]">*</span>
            <input
              type="email"
              value={form.email}
              onChange={(event) => handleChange("email", event.target.value)}
              onBlur={() => handleBlur("email")}
              maxLength={120}
              required
              className={`mt-2 w-full rounded-2xl border px-4 py-3 text-sm text-[#2d221b] outline-none transition-colors ${
                touched.email && errors.email
                  ? "border-red-400 bg-red-50/40 focus:border-red-500"
                  : "border-[#d8c8b9] bg-white focus:border-[#b85c38] focus:ring-2 focus:ring-[#b85c38]/15"
              }`}
              autoComplete="email"
              placeholder="votre.email@exemple.fr"
            />
            {touched.email && errors.email && (
              <span className="mt-1.5 block text-xs font-normal normal-case text-red-600">{errors.email}</span>
            )}
          </label>

          <label className="mt-5 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5e4b3e]">
            Téléphone <span className="text-xs font-normal normal-case text-[#8c6d58]">(optionnel)</span>
            <input
              type="tel"
              value={form.phone}
              onChange={(event) => handleChange("phone", event.target.value)}
              onBlur={() => handleBlur("phone")}
              maxLength={25}
              className={`mt-2 w-full rounded-2xl border px-4 py-3 text-sm text-[#2d221b] outline-none transition-colors ${
                touched.phone && errors.phone
                  ? "border-red-400 bg-red-50/40 focus:border-red-500"
                  : "border-[#d8c8b9] bg-white focus:border-[#b85c38] focus:ring-2 focus:ring-[#b85c38]/15"
              }`}
              autoComplete="tel"
              placeholder="06 12 34 56 78"
            />
            {touched.phone && errors.phone && (
              <span className="mt-1.5 block text-xs font-normal normal-case text-red-600">{errors.phone}</span>
            )}
          </label>

          <label className="mt-5 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5e4b3e]">
            Où en êtes-vous ?
            <select
              value={form.moment}
              onChange={(event) => {
                const nextMoment = event.target.value
                setForm((prev) => ({
                  ...prev,
                  moment: nextMoment,
                  babyWorkshop: nextMoment === "Atelier bébé" ? prev.babyWorkshop : "",
                }))
              }}
              className="mt-2 w-full rounded-2xl border border-[#d8c8b9] bg-white px-4 py-3 text-sm text-[#2d221b] outline-none focus:border-[#b85c38] focus:ring-2 focus:ring-[#b85c38]/15"
            >
              <option>Grossesse</option>
              <option>Préparation à la naissance</option>
              <option>Post-partum</option>
              <option>Désir d'enfant / Pré-conception</option>
              <option>Atelier bébé</option>
              <option>Grossesse arrêtée</option>
              <option>Deuil périnatal</option>
              <option>Carte Cadeau</option>
              <option>Je ne sais pas encore</option>
            </select>
          </label>

          {/* Champs spécifiques Carte Cadeau */}
          {form.moment === "Atelier bébé" ? (
            <div className="mt-5 rounded-2xl border border-[#eed9cb] bg-[#f7efe6] p-4 sm:p-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#b85c38]">
                <Baby size={15} />
                <span>Atelier bébé</span>
              </div>

              <label className="mt-3 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5e4b3e]">
                Atelier souhaité
                <select
                  value={form.babyWorkshop}
                  onChange={(event) => setForm((prev) => ({ ...prev, babyWorkshop: event.target.value }))}
                  className="mt-2 w-full rounded-2xl border border-[#d8c8b9] bg-white px-4 py-3 text-sm text-[#2d221b] outline-none focus:border-[#b85c38] focus:ring-2 focus:ring-[#b85c38]/15"
                >
                  <option value="">-- Choisissez un atelier --</option>
                  {prestationsBebe.map((atelier) => (
                    <optgroup key={atelier.id} label={atelier.title}>
                      {(atelier.pricing || []).map((tarif) => {
                        const optionValue = `${atelier.title} — ${tarif.label} (${tarif.price})`
                        return (
                          <option key={`${atelier.id}-${tarif.label}`} value={optionValue}>
                            {optionValue}
                          </option>
                        )
                      })}
                    </optgroup>
                  ))}
                </select>
              </label>
            </div>
          ) : null}

          {form.moment === "Carte Cadeau" && (
            <div className="mt-5 rounded-2xl border border-[#eed9cb] bg-[#f7efe6] p-4 sm:p-5">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#b85c38]">
                <Gift size={15} />
                <span>Détails de la Carte Cadeau</span>
              </div>

              <label className="mt-3 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5e4b3e]">
                Prestation à offrir
                <select
                  value={form.giftPrestation}
                  onChange={(event) => setForm((prev) => ({ ...prev, giftPrestation: event.target.value }))}
                  className="mt-2 w-full rounded-2xl border border-[#d8c8b9] bg-white px-4 py-3 text-sm text-[#2d221b] outline-none focus:border-[#b85c38] focus:ring-2 focus:ring-[#b85c38]/15"
                >
                  <option value="">-- Choisissez une prestation ou formule --</option>
                  <optgroup label="Massages Maman">
                    <option value="Massage Pré-conception (1h15) - 70 €">Massage Pré-conception (1h15) - 70 €</option>
                    <option value="Massage &quot;Bien-Naître&quot; (prénatal 1h15) - 70 €">Massage "Bien-Naître" (prénatal 1h15) - 70 €</option>
                    <option value="Massage Post-natal + Resserrage de Bassin (1h30) - 80 €">Massage Post-natal + Resserrage de Bassin (1h30) - 80 €</option>
                  </optgroup>
                  <optgroup label="Formules">
                    <option value="Formule Duo (2 massages au choix) - 130 €">Formule Duo (2 massages au choix) - 130 €</option>
                    <option value="Formule Complète (3 massages) - 200 €">Formule Complète (3 massages) - 200 €</option>
                  </optgroup>
                  <optgroup label="Accompagnement Doula">
                    <option value="Accompagnement Doula (séance découverte ou accompagnement)">Accompagnement Doula (séance découverte ou accompagnement)</option>
                    <option value="Formule 3 séances Doula - 170 €">Formule 3 séances Doula - 170 €</option>
                    <option value="Formule Post-partum Doula (3 séances) - 260 €">Formule Post-partum Doula (3 séances) - 260 €</option>
                    <option value="Formule Naissance Doula - 380 €">Formule Naissance Doula - 380 €</option>
                  </optgroup>
                  <optgroup label="Prestations Bébé">
                    <option value="Atelier Massage Bébé - Dès 20 € (collectif) / 50 € (individuel)">Atelier Massage Bébé - Dès 20 € (collectif) / 50 € (individuel)</option>
                    <option value="Atelier Portage - Dès 20 € (collectif) / 50 € (individuel)">Atelier Portage - Dès 20 € (collectif) / 50 € (individuel)</option>
                    <option value="Atelier Baby Yoga - Dès 20 € (collectif) / 50 € (individuel)">Atelier Baby Yoga - Dès 20 € (collectif) / 50 € (individuel)</option>
                    <option value="Atelier réflexologie émotionnelle - 50 €">Atelier réflexologie émotionnelle - 50 €</option>
                  </optgroup>
                  <optgroup label="Autre">
                    <option value="Montant personnalisé / Bon cadeau libre">Montant personnalisé / Bon cadeau libre</option>
                  </optgroup>
                </select>
              </label>

              <label className="mt-3.5 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5e4b3e]">
                Prénom du / de la bénéficiaire <span className="text-xs font-normal normal-case text-[#8c6d58]">(optionnel)</span>
                <input
                  type="text"
                  value={form.recipientName}
                  onChange={(event) => setForm((prev) => ({ ...prev, recipientName: event.target.value }))}
                  maxLength={80}
                  className="mt-2 w-full rounded-2xl border border-[#d8c8b9] bg-white px-4 py-3 text-sm text-[#2d221b] outline-none focus:border-[#b85c38] focus:ring-2 focus:ring-[#b85c38]/15"
                  placeholder="Ex : Julie, les futurs parents..."
                />
              </label>
            </div>
          )}

          <label className="mt-5 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5e4b3e]">
            Message <span className="text-[#b85c38]">*</span>
            <textarea
              value={form.message}
              onChange={(event) => handleChange("message", event.target.value)}
              onBlur={() => handleBlur("message")}
              rows={5}
              maxLength={3000}
              required
              className={`mt-2 w-full resize-y rounded-2xl border px-4 py-3 text-sm text-[#2d221b] outline-none transition-colors ${
                touched.message && errors.message
                  ? "border-red-400 bg-red-50/40 focus:border-red-500"
                  : "border-[#d8c8b9] bg-white focus:border-[#b85c38] focus:ring-2 focus:ring-[#b85c38]/15"
              }`}
              placeholder="Partagez-moi vos besoins, vos questions ou votre terme prévu..."
            />
            <div className="mt-1.5 flex items-center justify-between text-xs">
              {touched.message && errors.message ? (
                <span className="font-normal normal-case text-red-600">{errors.message}</span>
              ) : (
                <span className="text-[#8c6d58] normal-case">Au moins 10 caractères</span>
              )}
              <span className="text-[#8c6d58] normal-case">{form.message.length} / 3000</span>
            </div>
          </label>

          <button
            type="submit"
            disabled={sending}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#b85c38] to-[#9d4c2e] px-6 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-white shadow-warm-sm transition-all hover:shadow-warm-md hover:scale-[1.01] disabled:opacity-60"
          >
            <Send size={14} />
            <span>{sending ? "Envoi en cours..." : "Envoyer mon message"}</span>
          </button>

          <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-[#8c6d58]">
            <ShieldCheck size={14} className="text-[#b85c38]" />
            <span>Vos données restent strictement confidentielles et sécurisées.</span>
          </div>
        </form>
      </section>
    </div>
  )
}

export default Contact
